/**
 * Instrument Engine - TypeScript audio mapping layer
 *
 * Provides typed access to the audio manifest and helpers for
 * resolving notes to audio URLs.
 */

// Import the manifest (generated by build:audio-manifest script)
import manifestData from './audioManifest.json';

// Types for the manifest structure
interface SampleMap {
  [pitchId: string]: string;
}

interface InstrumentEntry {
  family: string;
  preset: string;
  samples: SampleMap;
}

interface AudioManifest {
  version: string;
  instruments: {
    [instrumentId: string]: InstrumentEntry;
  };
}

// Cast the imported manifest to our typed interface
const manifest = manifestData as AudioManifest;

/**
 * Instrument ID type - all available instrument identifiers
 * Using string type to allow dynamic instrument IDs from the manifest
 */
export type InstrumentId = string;

/**
 * Pitch ID type - can be a musical pitch (C4, Cs4, Db3) or sample_XX
 */
export type PitchId = string;

/**
 * Note interface matching the flashcard app's concept of a note
 */
export interface Note {
  midiNumber: number;
  pitchClass: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  accidental?: 'sharp' | 'flat' | 'natural';
  octave: number;
}

/**
 * Get all available instrument IDs
 */
export function getAvailableInstruments(): string[] {
  return Object.keys(manifest.instruments);
}

/**
 * Get metadata for a specific instrument
 */
export function getInstrumentMeta(id: InstrumentId): { family: string; preset: string } {
  const entry = manifest.instruments[id];
  if (!entry) {
    throw new Error(`Unknown instrument: ${id}`);
  }
  return {
    family: entry.family,
    preset: entry.preset
  };
}

/**
 * Get display name for an instrument (formatted nicely)
 */
export function getInstrumentDisplayName(id: InstrumentId): string {
  const meta = getInstrumentMeta(id);
  // Capitalize first letter of each word
  const formatWord = (s: string) =>
    s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return `${formatWord(meta.family)} - ${formatWord(meta.preset)}`;
}

/**
 * Get all available pitch IDs for an instrument
 */
export function getAvailablePitches(id: InstrumentId): PitchId[] {
  const entry = manifest.instruments[id];
  if (!entry) {
    return [];
  }
  return Object.keys(entry.samples);
}

/**
 * Get the audio URL for a specific instrument and pitch
 */
export function getAudioUrl(id: InstrumentId, pitch: PitchId): string | undefined {
  const entry = manifest.instruments[id];
  if (!entry) {
    return undefined;
  }
  return entry.samples[pitch];
}

/**
 * Convert a Note to a pitch ID string
 *
 * Examples:
 * - { pitchClass: 'C', octave: 4, accidental: 'natural' } -> 'C4'
 * - { pitchClass: 'C', octave: 4, accidental: 'sharp' } -> 'Cs4'
 * - { pitchClass: 'D', octave: 3, accidental: 'flat' } -> 'Db3'
 */
function noteToPitchId(note: Note): string {
  let accidentalSuffix = '';
  if (note.accidental === 'sharp') {
    accidentalSuffix = 's';
  } else if (note.accidental === 'flat') {
    accidentalSuffix = 'b';
  }
  return `${note.pitchClass}${accidentalSuffix}${note.octave}`;
}

/**
 * Get the enharmonic equivalent of a pitch
 *
 * For sharps: Cs -> Db, Ds -> Eb, Fs -> Gb, Gs -> Ab, As -> Bb
 * For flats: Db -> Cs, Eb -> Ds, Gb -> Fs, Ab -> Gs, Bb -> As
 */
function getEnharmonic(pitchId: string): string | null {
  // Parse the pitch
  const match = pitchId.match(/^([A-G])([sb])?(\d)$/);
  if (!match) return null;

  const [, note, accidental, octave] = match;

  // Sharp to flat mappings
  const sharpToFlat: Record<string, string> = {
    'Cs': 'Db', 'Ds': 'Eb', 'Fs': 'Gb', 'Gs': 'Ab', 'As': 'Bb'
  };

  // Flat to sharp mappings
  const flatToSharp: Record<string, string> = {
    'Db': 'Cs', 'Eb': 'Ds', 'Gb': 'Fs', 'Ab': 'Gs', 'Bb': 'As'
  };

  const noteWithAccidental = `${note}${accidental || ''}`;

  if (accidental === 's' && sharpToFlat[noteWithAccidental]) {
    return `${sharpToFlat[noteWithAccidental]}${octave}`;
  }

  if (accidental === 'b' && flatToSharp[noteWithAccidental]) {
    return `${flatToSharp[noteWithAccidental]}${octave}`;
  }

  return null;
}

/**
 * Resolve a Note to a PitchId for a given instrument
 *
 * Resolution strategy:
 * 1. Try exact pitch match (e.g., C4)
 * 2. Try enharmonic equivalent (e.g., Cs4 <-> Db4)
 * 3. Fall back to sample_XX based on MIDI number
 */
export function resolvePitchForNote(id: InstrumentId, note: Note): PitchId | undefined {
  const entry = manifest.instruments[id];
  if (!entry) {
    return undefined;
  }

  const samples = entry.samples;
  const availablePitches = Object.keys(samples);

  // 1. Try exact pitch match
  const exactPitch = noteToPitchId(note);
  if (samples[exactPitch]) {
    return exactPitch;
  }

  // 2. Try enharmonic equivalent
  const enharmonic = getEnharmonic(exactPitch);
  if (enharmonic && samples[enharmonic]) {
    return enharmonic;
  }

  // 3. Fall back to sample_XX based on MIDI number
  // Get all sample_XX pitches
  const samplePitches = availablePitches
    .filter(p => p.startsWith('sample_'))
    .sort();

  if (samplePitches.length > 0) {
    // Use MIDI number modulo number of samples for deterministic selection
    const index = note.midiNumber % samplePitches.length;
    return samplePitches[index];
  }

  // No samples available
  return undefined;
}

/**
 * Main function: Get the audio URL for a note with a specific instrument
 *
 * @param instrument - The instrument ID
 * @param note - The note to play
 * @returns The URL of the audio file, or undefined if not available
 */
export function getNoteAudioUrl(instrument: InstrumentId, note: Note): string | undefined {
  const pitchId = resolvePitchForNote(instrument, note);
  if (!pitchId) {
    return undefined;
  }
  return getAudioUrl(instrument, pitchId);
}

/**
 * Get instruments grouped by family
 */
export function getInstrumentsByFamily(): Map<string, InstrumentId[]> {
  const familyMap = new Map<string, InstrumentId[]>();

  for (const id of getAvailableInstruments()) {
    const meta = getInstrumentMeta(id);
    const family = meta.family;

    if (!familyMap.has(family)) {
      familyMap.set(family, []);
    }
    familyMap.get(family)!.push(id);
  }

  return familyMap;
}

/**
 * Default instrument to use when none is selected
 */
export const DEFAULT_INSTRUMENT: InstrumentId = 'keyboard_grand' as InstrumentId;
